/**
 * TimeStep — Compact time slot grid
 *
 * Arrow key navigation in time slot grid (roving tabindex),
 * aria-selected on slots, aria-label with time + price + availability.
 */

import { useMemo, useState, useCallback, useRef } from 'preact/hooks'
import { Clock } from '../components/icons'
import type { TimeSlot } from '../types'
import { formatPrice } from '../mock-data'

interface TimeStepProps {
  timeSlots: TimeSlot[]
  selectedTimeSlot: TimeSlot | null
  selectedDate: Date | null
  onSelectTimeSlot: (slot: TimeSlot) => void
  currencySymbol?: string
  timezone?: string
}

function formatDateCompact(date: Date): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`
}


export function TimeStep({ timeSlots, selectedTimeSlot, selectedDate, onSelectTimeSlot, currencySymbol = '$', timezone }: TimeStepProps) {
  const tz = timezone || 'Australia/Melbourne'

  const formatTime = (isoString: string): string => {
    try {
      const date = new Date(isoString)
      return new Intl.DateTimeFormat('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: tz,
      }).format(date)
    } catch {
      // Fallback if timezone is invalid
      const date = new Date(isoString)
      const hours = date.getHours()
      const minutes = date.getMinutes().toString().padStart(2, '0')
      const ampm = hours >= 12 ? 'PM' : 'AM'
      const displayHours = hours % 12 || 12
      return `${displayHours}:${minutes} ${ampm}`
    }
  }

  const formatSlotLabel = (slot: TimeSlot, symbol: string): string => {
    const time = formatTime(slot.time)
    const price = formatPrice(slot.domesticPrice, symbol)
    const spots = `${slot.remainingSlots} spot${slot.remainingSlots !== 1 ? 's' : ''} left`
    const parts = [time, price, spots]
    if (slot.clubNote) parts.push(slot.clubNote)
    if (!slot.available) parts.push('unavailable')
    return parts.join(', ')
  }

  const [focusedSlotIndex, setFocusedSlotIndex] = useState<number>(-1)
  const gridRef = useRef<HTMLDivElement>(null)

  const groupedSlots = useMemo(() => {
    const groups: Record<string, { courseName: string; slots: TimeSlot[] }> = {}
    timeSlots.forEach(slot => {
      const key = slot.courseName || 'default'
      if (!groups[key]) groups[key] = { courseName: slot.courseName || 'Available', slots: [] }
      groups[key].slots.push(slot)
    })
    return Object.values(groups)
  }, [timeSlots])

  const allSlots = useMemo(() => timeSlots, [timeSlots])
  const availableCount = timeSlots.filter(s => s.available).length
  const COLUMNS = 3

  const handleSlotKeyDown = useCallback((e: KeyboardEvent, flatIndex: number) => {
    let nextIndex = flatIndex

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault()
        nextIndex = flatIndex + 1
        break
      case 'ArrowLeft':
        e.preventDefault()
        nextIndex = flatIndex - 1
        break
      case 'ArrowDown':
        e.preventDefault()
        nextIndex = flatIndex + COLUMNS
        break
      case 'ArrowUp':
        e.preventDefault()
        nextIndex = flatIndex - COLUMNS
        break
      case 'Home':
        e.preventDefault()
        nextIndex = 0
        break
      case 'End':
        e.preventDefault()
        nextIndex = allSlots.length - 1
        break
      default:
        return
    }

    if (nextIndex >= 0 && nextIndex < allSlots.length) {
      setFocusedSlotIndex(nextIndex)
      const buttons = gridRef.current?.querySelectorAll<HTMLButtonElement>('button[data-slot-index]')
      buttons?.forEach(btn => {
        if (btn.dataset.slotIndex === String(nextIndex)) btn.focus()
      })
    }
  }, [allSlots.length])

  let flatCounter = 0

  return (
    <div>
      <div className="bw2-section-label">
        {selectedDate && formatDateCompact(selectedDate)}
        {' · '}
        {availableCount} slot{availableCount !== 1 ? 's' : ''}
      </div>

      {groupedSlots.length === 0 ? (
        <div className="bw2-recessed" style={{ textAlign: 'center', padding: '24px 12px' }} role="status">
          <Clock size={20} style={{ margin: '0 auto 8px', opacity: 0.3, color: 'var(--bw2-text-muted)' }} />
          <div style={{ fontSize: '10px', color: 'var(--bw2-text-muted)' }}>No tee times available</div>
        </div>
      ) : (
        groupedSlots.map(group => (
          <div key={group.courseName} style={{ marginBottom: '8px' }}>
            {groupedSlots.length > 1 && (
              <div style={{
                fontSize: '9px', fontWeight: 600, color: 'var(--bw2-text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.1em',
                marginBottom: '4px', fontFamily: 'var(--bw2-font-label)',
              }}>
                {group.courseName}
              </div>
            )}
            <div className="bw2-time-slots" ref={gridRef} role="listbox" aria-label="Available tee times">
              {group.slots.map(slot => {
                const currentFlatIndex = flatCounter++
                const isSelected = selectedTimeSlot?.id === slot.id
                const isUnavailable = !slot.available
                const isFocused = focusedSlotIndex === currentFlatIndex
                const classes = [
                  'bw2-time-slot',
                  isSelected && 'bw2-time-slot--selected',
                  isUnavailable && 'bw2-time-slot--unavailable',
                ].filter(Boolean).join(' ')

                return (
                  <button
                    key={slot.id}
                    className={classes}
                    onClick={() => !isUnavailable && onSelectTimeSlot(slot)}
                    onFocus={() => setFocusedSlotIndex(currentFlatIndex)}
                    onKeyDown={(e) => handleSlotKeyDown(e as unknown as KeyboardEvent, currentFlatIndex)}
                    disabled={isUnavailable}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    aria-label={formatSlotLabel(slot, currencySymbol)}
                    tabIndex={isSelected || isFocused || (focusedSlotIndex === -1 && currentFlatIndex === 0) ? 0 : -1}
                    data-slot-index={currentFlatIndex}
                  >
                    <div className="bw2-time-slot__time">{formatTime(slot.time)}</div>
                    <div className="bw2-time-slot__slots">{slot.remainingSlots} left</div>
                    <div className="bw2-time-slot__price">{formatPrice(slot.domesticPrice, currencySymbol)}</div>
                    {slot.clubNote && <div className="bw2-time-slot__note">{slot.clubNote}</div>}
                  </button>
                )
              })}
            </div>
          </div>
        ))
      )}
    </div>
  )
}
